import { Dispatch, FunctionComponent, SetStateAction, useState } from "react";

import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { Spinner } from "@/components/Spinner/Spinner";
import { GlobalSearchModal } from "@/components/GlobalSearchModal/GlobalSearchModal";

import { useAddExistingProspectToListMutation } from "@/pages/Private/redux/list/list.api";
import { extractDomain } from "@/utils/extractDomain";

import { Organization } from "../../../../Apollo/schema/get-people";
import {
	OrganizationProfile,
	OrganizationProfilesResponse,
} from "../../../schema/organization-profiles";
import { ProfileCard } from "./ProfileCard";

interface ListProfilesTabProps {
	data?: OrganizationProfilesResponse;
	isLoading: boolean;
	listId?: number;
	listName?: string;
	organization?: Organization | null;
	onModalOpenChange?: (isOpen: boolean) => void;
	/**
	 * Open a colleague's own lead panel. Hosts that can re-target a panel pass it; the cards are
	 * inert without it, rather than offering a click that leads nowhere (PREC-4116).
	 */
	onSelectProfile?: (profile: OrganizationProfile) => void;
}

export const ListProfilesTab: FunctionComponent<ListProfilesTabProps> = ({
	data,
	isLoading,
	listId,
	listName,
	organization,
	onModalOpenChange,
	onSelectProfile,
}) => {
	const { t } = useTranslation();
	const [showSearchPerson, setShowSearchPersonState] = useState(false);

	const setShowSearchPerson: Dispatch<SetStateAction<boolean>> = (value) => {
		const next = typeof value === "function" ? value(showSearchPerson) : value;

		setShowSearchPersonState(next);
		onModalOpenChange?.(next);
	};
	const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
	const [addExistingProspect, { isLoading: addExistingIsLoading }] =
		useAddExistingProspectToListMutation();
	const [addingProspectId, setAddingProspectId] = useState<number | null>(null);

	const handleAddExistingProspect = async (profile: OrganizationProfile) => {
		if (!listId) {
			return;
		}

		setAddingProspectId(profile.prospectId);

		try {
			await addExistingProspect({
				listId,
				prospectId: profile.prospectId,
			}).unwrap();
		} catch (error) {
			console.error("Failed to add prospect to list:", error);
		} finally {
			setAddingProspectId(null);
		}
	};

	// The "add person" modal is launched company-scoped: open the global search on its
	// filters tab with this organization's domain pre-applied, and add straight to this list.
	const companyDomain = organization?.primaryDomain || extractDomain(organization?.websiteUrl);
	const companyName = data?.organizationName || organization?.name || "";
	const initialFilterCompany = companyDomain
		? { name: companyName, domain: companyDomain }
		: undefined;
	const targetList = listId ? { id: listId, name: listName ?? "" } : undefined;

	if (isLoading) {
		return (
			<div className="flex justify-center py-8">
				<Spinner color="#4d66f5" />
			</div>
		);
	}

	if (!data || data.profiles.length === 0) {
		return (
			<div className="flex flex-col items-center gap-4 py-8">
				<p className="text-text-meta text-sm">{t("list.noOrganizationProfiles")}</p>
				<div className="max-w-[150px]">
					<Button
						size={ButtonSize.ML}
						title={t("list.addPerson")}
						onClick={() => setShowSearchPerson(true)}
					/>
				</div>
				{showSearchPerson && (
					<GlobalSearchModal
						suppressLeadPanel
						initialFilterCompany={initialFilterCompany}
						isOpened={showSearchPerson}
						setIsOpened={setShowSearchPerson}
						targetList={targetList}
					/>
				)}
			</div>
		);
	}

	return (
		<div className="mb-8 flex flex-col items-end">
			<div className="max-w-[150px] mb-4">
				<Button
					size={ButtonSize.ML}
					title={t("list.addPerson")}
					onClick={() => setShowSearchPerson(true)}
				/>
			</div>
			<div className="grid grid-cols-1 gap-3 w-full">
				{data.profiles.map((profile) => (
					<ProfileCard
						key={profile.prospectId}
						addIsLoading={addExistingIsLoading && addingProspectId === profile.prospectId}
						failedImages={failedImages}
						profile={profile}
						setFailedImages={setFailedImages}
						onAdd={() => handleAddExistingProspect(profile)}
						onSelect={onSelectProfile && (() => onSelectProfile(profile))}
					/>
				))}
			</div>
			{showSearchPerson && (
				<GlobalSearchModal
					suppressLeadPanel
					initialFilterCompany={initialFilterCompany}
					isOpened={showSearchPerson}
					setIsOpened={setShowSearchPerson}
					targetList={targetList}
				/>
			)}
		</div>
	);
};
